import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { User, Mail, Phone, Linkedin, Globe, Calendar, MessageSquare, Clock, TrendingUp } from "lucide-react";
import { STATUS_OPTIONS, INPUT_CLS, getStatusColor, formatDate } from "./leadDetailModel";

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">{Icon && <Icon className="h-3 w-3" />} {label}</label>
    {children}
  </div>
);

const EditableText = ({ icon, label, field, value, display, isEditing, setField, textarea, placeholder, className }) => (
  <Field icon={icon} label={label}>
    {isEditing ? (
      textarea
        ? <Textarea value={value} onChange={(e) => setField(field, e.target.value)} className={`${INPUT_CLS} min-h-[80px]`} placeholder={placeholder} />
        : <Input value={value} onChange={(e) => setField(field, e.target.value)} className={INPUT_CLS} />
    ) : display ?? <p className={className || "text-gray-300"}>{value || "Not specified"}</p>}
  </Field>
);

const ExternalLink = ({ icon, label, href, text }) => href ? (
  <Field icon={icon} label={label}>
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#00A38A] hover:underline text-sm truncate block">{text}</a>
  </Field>
) : null;

export const LeadInfoPanel = ({ lead, editData, isEditing, setField }) => (
  <div className="w-1/3 border-r border-gray-800 p-6 overflow-y-auto" data-testid="lead-info-panel">
    <h3 className="text-sm font-semibold text-gray-400 tracking-wide mb-4">Contact Information</h3>
    <div className="space-y-4">
      <EditableText icon={User} label="Contact Name" field="contactName" value={isEditing ? editData.contactName : lead.contactName} isEditing={isEditing} setField={setField} className="text-white font-medium" />
      <EditableText label="Title" field="contactTitle" value={isEditing ? editData.contactTitle : lead.contactTitle} isEditing={isEditing} setField={setField} />
      <EditableText icon={Mail} label="Email" field="email" value={isEditing ? editData.email : lead.email} isEditing={isEditing} setField={setField}
        display={<a href={`mailto:${lead.email}`} className="text-[#00A38A] hover:underline text-sm">{lead.email || "Not specified"}</a>} />
      <EditableText icon={Phone} label="Phone" field="phone" value={isEditing ? editData.phone : lead.phone} isEditing={isEditing} setField={setField} />
      <ExternalLink icon={Linkedin} label="LinkedIn" href={lead.linkedinUrl} text="View Profile →" />
      <ExternalLink icon={Globe} label="Website" href={lead.website} text={lead.website} />

      <Field label="Status">
        {isEditing ? (
          <select value={editData.status} onChange={(e) => setField("status", e.target.value)} className="w-full bg-white/5 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm" data-testid="lead-status-select">
            {[...new Set([editData.status, ...STATUS_OPTIONS].filter(Boolean))].map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        ) : <Badge className={`${getStatusColor(lead.status)} border`}>{lead.status}</Badge>}
      </Field>

      <Field icon={TrendingUp} label="Lead Score">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#00A38A] to-[#00A38A]" style={{ width: `${lead.score || 0}%` }} />
          </div>
          <span className="text-white font-semibold text-sm">{lead.score || 0}</span>
        </div>
      </Field>

      <EditableText icon={MessageSquare} label="Notes" field="notes" value={isEditing ? editData.notes : lead.notes} isEditing={isEditing} setField={setField} textarea
        placeholder="Add notes about this lead..." display={<p className="text-gray-400 text-sm">{lead.notes || "No notes"}</p>} />

      <div className="pt-4 border-t border-gray-800">
        <div className="flex items-center gap-2 text-xs text-gray-500"><Calendar className="h-3 w-3" /> Created: {formatDate(lead.createdAt)}</div>
        {lead.lastActivity && <div className="flex items-center gap-2 text-xs text-gray-500 mt-1"><Clock className="h-3 w-3" /> Last Activity: {formatDate(lead.lastActivity)}</div>}
      </div>
    </div>
  </div>
);
